/* sugar:assets/main-cart.js@2.0.0 */
/**
 * Cart Page
 * Behaviour for sections/main-cart.liquid — keeps the page's items, count and order summary in
 * step with every cart change, and drives the mobile summary sheet.
 *
 * Mutations are made by the shared cart components (sugar-cart-items, toggles, upsells, the
 * discount input); each one asks Shopify to render this section alongside the drawer and
 * publishes the result, so the page patches itself from that same round trip and only fetches
 * when a publisher did not carry the sections.
 */
if (!customElements.get('sugar-cart-page')) {
  class SugarCartPage extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.requestId = 0;
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onDocumentCartUpdate = this.onDocumentCartUpdate.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onClick = this.onClick.bind(this);
    }

    connectedCallback() {
      if (window.__swOk === false) return;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
      document.addEventListener('cart:update', this.onDocumentCartUpdate);
      this.addEventListener('click', this.onClick);
      this.setupSheet();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener('cart:update', this.onDocumentCartUpdate);
      document.removeEventListener('keydown', this.onKeyDown);
      this.removeEventListener('click', this.onClick);
      if (this.sheetObserver) this.sheetObserver.disconnect();
    }

    /* ── Cart sync ─────────────────────────────────────────────────────── */

    onCartUpdate(event) {
      const sections = event && event.sections;
      if (sections && typeof sections[this.sectionId] === 'string') {
        this.applyHtml(sections[this.sectionId]);
        return;
      }
      this.fetchAndApply();
    }

    // Publishers outside the pub/sub bus (legacy apps, the media block) only say "changed".
    onDocumentCartUpdate(event) {
      const detail = event && event.detail;
      if (detail && detail.sections && typeof detail.sections[this.sectionId] === 'string') {
        this.applyHtml(detail.sections[this.sectionId]);
        return;
      }
      this.fetchAndApply();
    }

    fetchAndApply() {
      const requestId = ++this.requestId;
      const base = (window.routes && window.routes.cart_url) || '/cart';
      const url = `${base}?sections=${encodeURIComponent(this.sectionId)}`;
      // Let a mutation that is still settling land first (30 ms, like the drawer).
      clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(() => {
        fetch(url, { headers: { Accept: 'application/json' } })
          .then((response) => response.json())
          .then((sections) => {
            if (requestId !== this.requestId) return;
            if (sections && typeof sections[this.sectionId] === 'string') this.applyHtml(sections[this.sectionId]);
          })
          .catch(() => {});
      }, 30);
    }

    applyHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('sugar-cart-page');
      if (!source) return;
      this.patchFrom(source);
    }

    // Patch in place so the summary's components (toggles, discount input, progress bars)
    // keep their state; only the parts that show cart numbers are rewritten.
    patchFrom(source) {
      const isEmpty = source.classList.contains('is-empty');
      this.classList.toggle('is-empty', isEmpty);
      if (isEmpty) this.closeSheet();
      if (source.dataset.cartSig) this.dataset.cartSig = source.dataset.cartSig;

      const itemsWrapper = this.querySelector('sugar-cart-items');
      if (itemsWrapper) itemsWrapper.classList.toggle('is-empty', isEmpty);

      [
        '[data-mc-count]',
        '[data-mc-sheet-total]',
        '.sugar-cart-drawer__items',
        '.sugar-cart-subtotals',
        '.sugar-cart-checkout-button',
        '[data-sugar-installments]',
      ].forEach((selector) => this.syncInner(selector, source));

      const targets = this.querySelectorAll('sugar-cart-progress');
      const sources = source.querySelectorAll('sugar-cart-progress');
      if (targets.length === sources.length && typeof sugarCartMorphProgress === 'function') {
        targets.forEach((target, index) => sugarCartMorphProgress(target, sources[index]));
      }

      this.measureSheet();
    }

    syncInner(selector, source) {
      const targets = this.querySelectorAll(selector);
      const sources = source.querySelectorAll(selector);
      if (targets.length !== sources.length) return false;
      targets.forEach((target, index) => {
        const next = sources[index].innerHTML;
        if (target.innerHTML !== next) target.innerHTML = next;
      });
      return true;
    }

    /* ── Mobile summary sheet ──────────────────────────────────────────── */

    get isSheet() {
      return this.dataset.mobileSummary === 'sheet';
    }

    setupSheet() {
      if (!this.isSheet) return;
      this.summary = this.querySelector('[data-mc-summary]');
      this.sheetToggle = this.querySelector('[data-mc-sheet-toggle]');
      if (!this.summary || !this.sheetToggle) return;

      document.addEventListener('keydown', this.onKeyDown);
      if (typeof ResizeObserver === 'function') {
        this.sheetObserver = new ResizeObserver(() => this.measureSheet());
        this.sheetObserver.observe(this.summary);
      }
      this.measureSheet();
    }

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-mc-sheet-toggle]')) {
        event.preventDefault();
        this.toggleSheet();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape' && this.hasAttribute('data-sheet-open')) this.closeSheet();
    }

    toggleSheet() {
      if (this.hasAttribute('data-sheet-open')) this.closeSheet();
      else this.openSheet();
    }

    openSheet() {
      if (!this.isSheet) return;
      this.setAttribute('data-sheet-open', '');
      if (this.sheetToggle) this.sheetToggle.setAttribute('aria-expanded', 'true');
    }

    closeSheet() {
      if (!this.isSheet) return;
      this.removeAttribute('data-sheet-open');
      if (this.sheetToggle) this.sheetToggle.setAttribute('aria-expanded', 'false');
      this.measureSheet();
    }

    // The collapsed sheet covers the bottom of the page; the page keeps that much room free.
    measureSheet() {
      if (!this.isSheet || !this.summary) return;
      if (this.hasAttribute('data-sheet-open')) return;
      const style = window.getComputedStyle(this.summary);
      if (style.position !== 'fixed') {
        this.style.removeProperty('--mc-sheet-h');
        return;
      }
      const height = Math.ceil(this.summary.getBoundingClientRect().height);
      if (height > 0) this.style.setProperty('--mc-sheet-h', `${height}px`);
    }
  }

  customElements.define('sugar-cart-page', SugarCartPage);
}
